import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Truck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { fmt, codLineTotal } from "../constants/site";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, setQty, remove, count, subtotal, clear } = useCart();
  const navigate = useNavigate();

  const close = () => setIsOpen(false);
  const goCheckout = () => {
    if (window.fbq) window.fbq("track", "InitiateCheckout", { value: subtotal, currency: "INR", num_items: count });
    close();
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
            className="fixed inset-0 z-[80] bg-charcoal-900/45 backdrop-blur-sm"
            data-testid="cart-drawer-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[90] flex h-full w-[430px] max-w-[94vw] flex-col bg-oat-50 shadow-2xl"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-charcoal-900/10 px-6 py-5">
              <h3 className="font-serif text-2xl text-charcoal-900" data-testid="cart-drawer-title">
                Your Cart{" "}
                <span className="text-base text-charcoal-500">({count})</span>
              </h3>
              <button
                onClick={close}
                data-testid="cart-drawer-close-btn"
                aria-label="Close cart"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal-900/10 transition-colors hover:border-sage-500 hover:text-sage-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-sage-100">
                  <ShoppingBag className="h-9 w-9 text-sage-600" strokeWidth={1.4} />
                </span>
                <p className="font-serif text-2xl text-charcoal-900">Your cart is empty</p>
                <p className="max-w-[240px] text-sm leading-relaxed text-charcoal-500">
                  Add the OmniMist 2-in-1 Sprayer and start cooking with zero mess.
                </p>
                <button
                  onClick={close}
                  data-testid="cart-drawer-continue-btn"
                  className="mt-2 rounded-full bg-charcoal-900 px-7 py-3 text-xs font-medium uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="cart-drawer-items">
                  {items.map((i) => (
                    <div
                      key={i.id}
                      className="mb-4 flex gap-4 rounded-2xl border border-charcoal-900/10 bg-white p-4"
                      data-testid="cart-drawer-item"
                    >
                      <img
                        src={i.image}
                        alt={i.name}
                        loading="lazy"
                        decoding="async"
                        className="h-20 w-20 shrink-0 rounded-xl object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-charcoal-900">{i.name}</p>
                        <p className="mt-0.5 text-xs uppercase tracking-[0.14em] text-charcoal-500">
                          {i.capacity}
                        </p>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center gap-3 rounded-full border border-charcoal-900/15 px-3 py-1.5">
                            <button
                              onClick={() => setQty(i.id, i.qty - 1)}
                              data-testid="cart-item-qty-minus"
                              aria-label="Decrease quantity"
                              className="text-charcoal-500 transition-colors hover:text-sage-600"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="min-w-4 text-center text-sm font-medium" data-testid="cart-item-qty">
                              {i.qty}
                            </span>
                            <button
                              onClick={() => setQty(i.id, i.qty + 1)}
                              data-testid="cart-item-qty-plus"
                              aria-label="Increase quantity"
                              className="text-charcoal-500 transition-colors hover:text-sage-600"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <p className="font-serif text-lg text-charcoal-900">
                            {fmt(codLineTotal(i.qty))}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => remove(i.id)}
                        data-testid="cart-item-remove"
                        aria-label="Remove item"
                        className="self-start text-charcoal-500/60 transition-colors hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={clear}
                    data-testid="cart-clear-btn"
                    className="mx-auto mt-1 block text-xs uppercase tracking-[0.18em] text-charcoal-500/70 underline-offset-4 hover:underline"
                  >
                    Clear cart
                  </button>
                </div>

                <div className="border-t border-charcoal-900/10 px-6 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
                  <p className="mb-4 flex items-center gap-2 rounded-xl bg-sage-100 px-4 py-3 text-sm font-medium text-sage-700">
                    <Truck className="h-4 w-4" />
                    Free express shipping applied — arrives in 2–4 days
                  </p>
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm uppercase tracking-[0.16em] text-charcoal-500">
                      Subtotal
                    </span>
                    <span className="font-serif text-2xl text-charcoal-900" data-testid="cart-drawer-subtotal">
                      {fmt(subtotal)}
                    </span>
                  </div>
                  <button
                    onClick={goCheckout}
                    data-testid="cart-drawer-checkout-btn"
                    className="w-full rounded-full bg-charcoal-900 py-4 text-sm font-medium uppercase tracking-[0.16em] text-oat-100 transition-all duration-300 hover:bg-sage-600"
                  >
                    Checkout · {fmt(subtotal)}
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
